// Photos and documents in the private `company-files` bucket (WEB_PLAN.md §3.9).
//
// - Paths are exactly `SyncEngine.storagePath` (GroundTeam/Sync/SyncEngine.swift:567):
//   `<company lowercase>/<attachments|documents>/<fileName>`; the last segment IS the payload's `fileName`.
// - Display: signed URLs, asked for in batches (one createSignedUrls call for every thumbnail a screen shows) and
//   cached per path until 5 minutes before they expire. Storage RLS (`can_read_file`, M1:334) decides; a refusal or a
//   missing object shows "File not available" and is asked again after 5 minutes (SyncEngine.downloadRetryInterval).
// - `filesMode === 'download'` (the e2e fake has no /object/sign/ endpoint), and any batch whose signing call fails,
//   download the bytes instead and show them through an object URL.
// - Upload (Phase 2): upload first with `upsert: false`, then push the item with `file_path` (the uploader must be
//   the pusher, M1:600-613). The uploaded bytes are shown right away from memory.

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Collection } from '@/contracts/collections';

export const FILES_BUCKET = 'company-files';
/** Signed URL lifetime, seconds. */
export const SIGNED_URL_TTL_SECONDS = 3600;
/** A cached signed URL is refreshed this long before it expires. */
export const SIGNED_URL_REFRESH_MARGIN_MS = 5 * 60_000;
/** A file that could not be shown is asked for again after this long. */
export const FILE_RETRY_MS = 5 * 60_000;
/** Paths per createSignedUrls call. */
export const SIGN_BATCH_SIZE = 100;

/** `<company lowercase>/<collection>/<fileName>` (SyncEngine.storagePath). */
export function storagePath(companyId: string, collection: Extract<Collection, 'attachments' | 'documents'>, fileName: string): string {
  return `${companyId.toLowerCase()}/${collection}/${fileName}`;
}

/** SyncEngine.contentType(for:): the four types the bucket accepts. */
export function contentTypeFor(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const ext = dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'pdf':
      return 'application/pdf';
    default:
      return 'application/octet-stream';
  }
}

/** A storage failure: `missing` = refused or not found (400/404, RLS); otherwise worth retrying later. */
export class FileError extends Error {
  readonly missing: boolean;
  constructor(message: string, missing: boolean) {
    super(message);
    this.name = 'FileError';
    this.missing = missing;
  }
}

/** The storage calls files.ts needs (injectable for tests). */
export interface StorageApi {
  /** Signed URL per path; null for a path the caller cannot read or that does not exist. Throws when the call fails. */
  signUrls(paths: readonly string[], expiresInSeconds: number): Promise<Map<string, string | null>>;
  /** The object's bytes. Throws FileError (`missing` for 400/404). */
  download(path: string): Promise<Blob>;
  /** Uploads a new object (never overwrites). Throws FileError. */
  upload(path: string, body: Blob, contentType: string): Promise<void>;
}

function storageStatus(error: unknown): number | null {
  const status = (error as { status?: unknown; statusCode?: unknown } | null)?.status ?? (error as { statusCode?: unknown } | null)?.statusCode;
  if (typeof status === 'number') return status;
  if (typeof status === 'string' && /^\d+$/.test(status)) return Number(status);
  return null;
}

function isMissingStatus(status: number | null): boolean {
  return status === 400 || status === 403 || status === 404;
}

export function createSupabaseStorageApi(client: SupabaseClient, bucket: string = FILES_BUCKET): StorageApi {
  return {
    async signUrls(paths, expiresInSeconds) {
      const { data, error } = await client.storage.from(bucket).createSignedUrls([...paths], expiresInSeconds);
      if (error || !data) throw new FileError(error?.message ?? 'Signing failed', false);
      const out = new Map<string, string | null>();
      for (const entry of data) {
        if (entry.path) out.set(entry.path, entry.error || !entry.signedUrl ? null : entry.signedUrl);
      }
      for (const path of paths) if (!out.has(path)) out.set(path, null);
      return out;
    },
    async download(path) {
      const { data, error } = await client.storage.from(bucket).download(path);
      if (error || !data) {
        const status = storageStatus(error);
        throw new FileError(error?.message ?? 'Download failed', isMissingStatus(status));
      }
      return data;
    },
    async upload(path, body, contentType) {
      const { error } = await client.storage.from(bucket).upload(path, body, { upsert: false, contentType });
      if (error) throw new FileError(error.message, isMissingStatus(storageStatus(error)));
    },
  };
}

/** What a thumbnail shows. `stale`: the URL still works but is being (or should be) refreshed. */
export type FileUrlState =
  | { status: 'idle'; url: null }
  | { status: 'loading'; url: string | null }
  | { status: 'ready'; url: string; stale: boolean }
  | { status: 'missing'; url: null };

const IDLE: FileUrlState = { status: 'idle', url: null };

interface Entry {
  state: FileUrlState;
  /** Object URL to revoke when the entry goes. */
  objectUrl: string | null;
  timer: ReturnType<typeof setTimeout> | null;
}

export interface FileUrlsOptions {
  mode: 'signed' | 'download';
  /** For tests. */
  now?: () => number;
  createObjectUrl?: (blob: Blob) => string;
  revokeObjectUrl?: (url: string) => void;
}

/**
 * The URL cache screens read through `useFileUrl(path)`. Requests made in the same tick are signed in one batch;
 * snapshots are immutable and change identity only when a path's state changes (safe for useSyncExternalStore).
 */
export class FileUrls {
  private entries = new Map<string, Entry>();
  private queue = new Set<string>();
  private flushScheduled = false;
  private listeners = new Set<() => void>();
  private disposed = false;
  private readonly mode: 'signed' | 'download';
  private readonly now: () => number;
  private readonly createObjectUrl: (blob: Blob) => string;
  private readonly revokeObjectUrl: (url: string) => void;

  constructor(
    private readonly storage: StorageApi,
    options: FileUrlsOptions,
  ) {
    this.mode = options.mode;
    this.now = options.now ?? Date.now;
    this.createObjectUrl = options.createObjectUrl ?? ((blob) => URL.createObjectURL(blob));
    this.revokeObjectUrl = options.revokeObjectUrl ?? ((url) => URL.revokeObjectURL(url));
  }

  /** Current state of a path (IDLE when never asked for). */
  get = (path: string | null | undefined): FileUrlState => {
    if (!path) return IDLE;
    return this.entries.get(path)?.state ?? IDLE;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Asks for the given paths (idle, stale or due for a retry); everything asked in one tick goes in one batch. */
  request(paths: Iterable<string | null | undefined>): void {
    if (this.disposed) return;
    let changed = false;
    for (const path of paths) {
      if (!path || this.queue.has(path)) continue;
      const entry = this.entries.get(path);
      const state = entry?.state ?? IDLE;
      const wanted = state.status === 'idle' || (state.status === 'ready' && state.stale);
      if (!wanted) continue;
      this.queue.add(path);
      this.set(path, { status: 'loading', url: state.url }, entry?.objectUrl ?? null);
      changed = true;
    }
    if (changed) {
      this.emit();
      this.scheduleFlush();
    }
  }

  /**
   * Uploads a new file (`upsert: false`) and shows it from memory right away. Push the item with `file_path`
   * only after this resolves (the server checks the uploader owns the object).
   */
  async upload(path: string, body: Blob, contentType: string = contentTypeFor(path)): Promise<void> {
    await this.storage.upload(path, body, contentType);
    if (this.disposed) return;
    const url = this.createObjectUrl(body);
    this.set(path, { status: 'ready', url, stale: false }, url);
    this.emit();
  }

  /** Forgets a path (after its item was deleted). */
  forget(path: string): void {
    const entry = this.entries.get(path);
    if (!entry) return;
    this.drop(path, entry);
    this.emit();
  }

  /** Drops every URL and revokes object URLs (sign-out, company change). */
  clear(): void {
    for (const [path, entry] of this.entries) this.drop(path, entry);
    this.queue.clear();
    this.emit();
  }

  /** Clears and refuses further work. */
  dispose(): void {
    this.clear();
    this.disposed = true;
    this.listeners.clear();
  }

  /** Resolves when every queued path has been answered (tests). */
  async idle(): Promise<void> {
    while (this.flushScheduled || this.inFlight > 0) await new Promise((r) => setTimeout(r, 0));
  }

  // MARK: - Internals

  private inFlight = 0;

  private scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    setTimeout(() => {
      this.flushScheduled = false;
      void this.flush();
    }, 0);
  }

  private async flush(): Promise<void> {
    const paths = [...this.queue];
    this.queue.clear();
    if (paths.length === 0 || this.disposed) return;
    this.inFlight++;
    try {
      if (this.mode === 'download') {
        await Promise.all(paths.map((p) => this.downloadOne(p)));
        return;
      }
      for (let i = 0; i < paths.length; i += SIGN_BATCH_SIZE) {
        const batch = paths.slice(i, i + SIGN_BATCH_SIZE);
        let signed: Map<string, string | null>;
        try {
          signed = await this.storage.signUrls(batch, SIGNED_URL_TTL_SECONDS);
        } catch {
          // Signing unavailable (old fake server, transient failure): fetch the bytes instead.
          if (this.disposed) return;
          await Promise.all(batch.map((p) => this.downloadOne(p)));
          continue;
        }
        if (this.disposed) return;
        const refreshAt = this.now() + SIGNED_URL_TTL_SECONDS * 1000 - SIGNED_URL_REFRESH_MARGIN_MS;
        for (const path of batch) {
          const url = signed.get(path) ?? null;
          if (url) this.setReady(path, url, null, refreshAt);
          else this.setMissing(path);
        }
        this.emit();
      }
    } finally {
      this.inFlight--;
    }
  }

  private async downloadOne(path: string): Promise<void> {
    try {
      const blob = await this.storage.download(path);
      if (this.disposed) return;
      const url = this.createObjectUrl(blob);
      this.setReady(path, url, url, null);
    } catch {
      // Refused, missing or a transient failure: the tile says "File not available" and the path is asked for
      // again after FILE_RETRY_MS if it is still on screen.
      if (this.disposed) return;
      this.setMissing(path);
    }
    this.emit();
  }

  private setReady(path: string, url: string, objectUrl: string | null, refreshAt: number | null): void {
    const entry = this.set(path, { status: 'ready', url, stale: false }, objectUrl);
    if (refreshAt !== null) {
      entry.timer = setTimeout(() => {
        const current = this.entries.get(path);
        if (current !== entry || entry.state.status !== 'ready') return;
        entry.timer = null;
        entry.state = { status: 'ready', url: entry.state.url, stale: true };
        this.emit();
      }, Math.max(0, refreshAt - this.now()));
    }
  }

  private setMissing(path: string): void {
    const entry = this.set(path, { status: 'missing', url: null }, null);
    entry.timer = setTimeout(() => this.reset(path, entry), FILE_RETRY_MS);
  }

  private reset(path: string, entry: Entry): void {
    if (this.entries.get(path) !== entry) return;
    this.drop(path, entry);
    this.emit();
  }

  private set(path: string, state: FileUrlState, objectUrl: string | null): Entry {
    const old = this.entries.get(path);
    if (old?.timer) clearTimeout(old.timer);
    if (old?.objectUrl && old.objectUrl !== objectUrl) this.revokeObjectUrl(old.objectUrl);
    const entry: Entry = { state, objectUrl, timer: null };
    this.entries.set(path, entry);
    return entry;
  }

  private drop(path: string, entry: Entry): void {
    if (entry.timer) clearTimeout(entry.timer);
    if (entry.objectUrl) this.revokeObjectUrl(entry.objectUrl);
    this.entries.delete(path);
  }

  private emit(): void {
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (error) {
        console.error('file URL listener failed', error);
      }
    }
  }
}
